import {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useRef,
  useState,
} from 'react'

/** a mouse drag longer than this is a scroll, not a click on a bubble */
const DRAG_CLICK_SLOP_PX = 5

/**
 * Drag a row sideways with the mouse (touch scrolls natively). The click that
 * ends a drag is swallowed so it does not open the bubble under the pointer.
 */
export const useDragScroll = (ref: RefObject<HTMLElement>) => {
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null)
  const swallowClick = useRef(false)
  const [dragging, setDragging] = useState(false)

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !ref.current) {
      return
    }
    drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false }
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d || !ref.current) {
      return
    }
    const dx = e.clientX - d.x
    if (!d.moved && Math.abs(dx) > DRAG_CLICK_SLOP_PX) {
      d.moved = true
      setDragging(true)
    }
    if (d.moved) {
      ref.current.scrollLeft = d.left - dx
    }
  }

  const end = () => {
    if (drag.current?.moved) {
      swallowClick.current = true
      setDragging(false)
    }
    drag.current = null
  }

  const onClickCapture = (e: ReactMouseEvent<HTMLElement>) => {
    if (swallowClick.current) {
      swallowClick.current = false
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return {
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerLeave: end,
      onClickCapture,
    },
  }
}
